import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import {
  ActiveEmployerCompanyService,
  ActiveEmployerCompanyState,
} from '../active-employer-company/active-employer-company.service';
import { SessionService } from '../session/session.service';
import { TeamsService } from '../teams/teams.service';

export interface NativeTeamContextState {
  initialized: boolean;
  loading: boolean;
  active: boolean;
  companyId: string;
  teams: any[];
  error: string;
}

@Injectable({
  providedIn: 'root',
})
export class NativeTeamContextService {
  private readonly stateSubject = new BehaviorSubject<NativeTeamContextState>(
    this.getEmptyState(),
  );
  private initializePromise?: Promise<NativeTeamContextState>;
  private initializedUserId = '';
  private requestVersion = 0;

  constructor(
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private sessionService: SessionService,
    private teamsService: TeamsService,
  ) {}

  get state(): NativeTeamContextState {
    return this.stateSubject.value;
  }

  stateChanges(): BehaviorSubject<NativeTeamContextState> {
    return this.stateSubject;
  }

  initialize(forceRefresh = false): Promise<NativeTeamContextState> {
    const userId = this.sessionService.getUserId();
    if (this.initializedUserId && this.initializedUserId !== userId) {
      this.clear();
    }
    if (this.initializePromise) {
      return this.initializePromise;
    }
    if (
      !forceRefresh
      && this.state.initialized
      && this.initializedUserId === userId
    ) {
      return Promise.resolve(this.state);
    }

    const requestVersion = ++this.requestVersion;
    const promise = this.initializeForCurrentUser(userId, forceRefresh, requestVersion);
    this.initializePromise = promise;
    void promise.finally((): void => {
      if (this.initializePromise === promise) {
        this.initializePromise = undefined;
      }
    });
    return promise;
  }

  async selectTeam(teamId: string): Promise<any | null> {
    const normalizedTeamId = `${teamId || ''}`.trim();
    if (
      !this.state.active
      || !this.state.teams.some((team) => this.getTeamId(team) === normalizedTeamId)
    ) {
      return null;
    }

    const selectedTeam = await this.teamsService.setWellspace({ id: normalizedTeamId });
    if (
      !selectedTeam
      || !this.state.teams.some((team) => this.getTeamId(team) === this.getTeamId(selectedTeam))
    ) {
      this.teamsService.clearSelectedWellspace();
      return null;
    }
    return selectedTeam;
  }

  clear(): void {
    this.requestVersion++;
    if (this.state.active || this.state.loading) {
      this.teamsService.clearSelectedWellspace();
    }
    this.initializedUserId = '';
    this.initializePromise = undefined;
    this.stateSubject.next(this.getEmptyState());
  }

  private async initializeForCurrentUser(
    userId: string,
    forceRefresh: boolean,
    requestVersion: number,
  ): Promise<NativeTeamContextState> {
    const activeCompanyState = this.activeEmployerCompanyService.state;
    const companyState = activeCompanyState.workspaceContext === 'superadminCompany'
      ? await this.activeEmployerCompanyService.initializeScopedSuperadminCompany(
        `${activeCompanyState.activeCompany?.id || ''}`,
        forceRefresh,
      )
      : await this.activeEmployerCompanyService.initialize(forceRefresh);
    if (!this.isCurrentRequest(userId, requestVersion)) {
      return this.state;
    }

    if (!this.activeEmployerCompanyService.isSupportedSingleCompanyWorkspaceActive(companyState)) {
      if (this.state.active || this.state.loading) {
        this.teamsService.clearSelectedWellspace();
      }
      this.initializedUserId = userId;
      return this.replaceState({
        ...this.getEmptyState(),
        initialized: true,
      });
    }

    const companyId = this.getCompanyId(companyState);
    this.initializedUserId = userId;
    this.replaceState({
      initialized: false,
      loading: true,
      active: true,
      companyId,
      teams: [],
      error: '',
    });

    try {
      const response = await this.teamsService.getTeamsForCompany(companyId);
      if (!this.isCurrentRequest(userId, requestVersion)) {
        return this.state;
      }

      const teams = Array.isArray(response) ? [...response] : [];
      const authorizedTeams = teams.filter((team) => !!this.getTeamId(team));
      if (authorizedTeams.length !== teams.length) {
        return this.failClosed(
          companyId,
          [],
          'The company Team response contained a Team without an ID.',
        );
      }

      if (!authorizedTeams.length) {
        this.teamsService.clearSelectedWellspace();
        return this.replaceState({
          initialized: true,
          loading: false,
          active: true,
          companyId,
          teams: authorizedTeams,
          error: '',
        });
      }

      const authorizedTeamIds = new Set(authorizedTeams.map((team) => this.getTeamId(team)));
      const candidateTeamId = this.teamsService.getSelectedWellspaceCandidateId();
      if (candidateTeamId && !authorizedTeamIds.has(candidateTeamId)) {
        this.teamsService.clearSelectedWellspace();
      }
      const selectedTeamId = authorizedTeamIds.has(candidateTeamId)
        ? candidateTeamId
        : this.getTeamId(authorizedTeams[0]);
      const selectedTeam = await this.teamsService.setWellspace({ id: selectedTeamId });
      if (!this.isCurrentRequest(userId, requestVersion)) {
        return this.state;
      }
      if (!selectedTeam || !authorizedTeamIds.has(this.getTeamId(selectedTeam))) {
        return this.failClosed(
          companyId,
          authorizedTeams,
          'An authorized company Team could not be validated.',
        );
      }

      return this.replaceState({
        initialized: true,
        loading: false,
        active: true,
        companyId,
        teams: authorizedTeams,
        error: '',
      });
    } catch (error) {
      if (!this.isCurrentRequest(userId, requestVersion)) {
        return this.state;
      }
      return this.failClosed(
        companyId,
        [],
        'Unable to load Teams for the active employer company.',
      );
    }
  }

  private failClosed(
    companyId: string,
    teams: any[],
    error: string,
  ): NativeTeamContextState {
    this.teamsService.clearSelectedWellspace();
    return this.replaceState({
      initialized: true,
      loading: false,
      active: true,
      companyId,
      teams,
      error,
    });
  }

  private isCurrentRequest(userId: string, requestVersion: number): boolean {
    return requestVersion === this.requestVersion
      && userId === this.sessionService.getUserId();
  }

  private getCompanyId(state: ActiveEmployerCompanyState): string {
    return `${state.activeCompany?.id || ''}`.trim();
  }

  private getTeamId(team: any): string {
    return typeof team?.id === 'string' ? team.id.trim() : '';
  }

  private replaceState(state: NativeTeamContextState): NativeTeamContextState {
    this.stateSubject.next(state);
    return state;
  }

  private getEmptyState(): NativeTeamContextState {
    return {
      initialized: false,
      loading: false,
      active: false,
      companyId: '',
      teams: [],
      error: '',
    };
  }
}
